/**
 * Shared constants for the feedback panel (design spec unit-01-panel-design).
 * Limits mirror the server's handlers/feedback/constants.ts and ADR-11.
 */
import type { CSSProperties } from 'react';
import { TOPBAR_HEIGHT } from '@/components/shell/TopBar';

/**
 * The one place the panel layer is named (spec section 5.3). Above ToolFrame overlays (z-40),
 * below toasts, Radix poppers and dialogs (z-50) so the panel's own Select list always paints on top.
 */
export const FEEDBACK_LAYER_CLASS = 'z-[45]';

export const STORAGE_KEY = 'ic.feedback.panel';

export const MAX_IMAGES = 10;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_EDGE = 2560;
export const MAX_NOTE = 10000;
export const NOTE_COUNTER_FROM = 9000;
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg'] as const;

export type FeedbackCategory = 'bug' | 'visual' | 'data' | 'performance' | 'idea';
export type FeedbackSeverity = 'low' | 'medium' | 'high' | 'critical';
export type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export const CORNERS: readonly Corner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
export const DEFAULT_CORNER: Corner = 'top-right';

export const CATEGORY_OPTIONS: ReadonlyArray<{ value: FeedbackCategory; label: string }> = [
  { value: 'bug', label: 'Bug' },
  { value: 'visual', label: 'Visual' },
  { value: 'data', label: 'Data' },
  { value: 'performance', label: 'Performance' },
  { value: 'idea', label: 'Idea' },
];

export const SEVERITY_OPTIONS: ReadonlyArray<{ value: FeedbackSeverity; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/** Gap below the TopBar for top corners (spacing(2) = 8px) and from the edges (spacing(3) = 12px). */
const TOP_GAP_PX = 8;
const EDGE_GAP_PX = 12;

/**
 * Read lazily: TopBar imports FeedbackFlag, which imports this module, so TOPBAR_HEIGHT is not yet
 * initialised while this module first evaluates. It is only needed at render time.
 */
const topOffset = (): number => TOPBAR_HEIGHT + TOP_GAP_PX;

/** Inline anchor style for a corner (spec section 6.1): CSS anchors, never {x,y}. */
export function anchorStyle(corner: Corner): CSSProperties {
  const style: CSSProperties = {};
  if (corner.startsWith('top')) style.top = topOffset();
  else style.bottom = EDGE_GAP_PX;
  if (corner.endsWith('left')) style.left = EDGE_GAP_PX;
  else style.right = EDGE_GAP_PX;
  return style;
}

export const panelMaxHeight = (): string => `calc(100vh - ${topOffset() + EDGE_GAP_PX}px)`;

export const CORNER_TEST_ID: Record<Corner, string> = {
  'top-left': 'tl',
  'top-right': 'tr',
  'bottom-left': 'bl',
  'bottom-right': 'br',
};
